/**
 * 🛡️ G2GENTE - Detalhe da Vaga
 * Exibe título, empresa, descrição, requisitos, local, perfil DISC
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { jobsAPI, candidatesAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { 
  MapPin, 
  Building2, 
  ArrowLeft,
  Loader2,
  Calendar,
  Briefcase,
  Star,
  Clock,
  CheckCircle2,
  Bookmark,
  BookmarkCheck,
  Brain,
  Users,
  ListChecks,
  Target
} from 'lucide-react';
import { toast } from 'sonner';

const DISC_LABELS = {
  D: { name: 'Dominância', color: 'bg-red-100 text-red-700' },
  I: { name: 'Influência', color: 'bg-amber-100 text-amber-700' },
  S: { name: 'Estabilidade', color: 'bg-emerald-100 text-emerald-700' },
  C: { name: 'Conformidade', color: 'bg-blue-100 text-blue-700' }
};

export default function MobileJobDetail() {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savingJob, setSavingJob] = useState(false);

  useEffect(() => {
    fetchJob();
    checkSavedStatus();
  }, [jobId]);

  const fetchJob = async () => {
    try {
      const response = await jobsAPI.getById(jobId);
      setJob(response.data);
    } catch (error) {
      toast.error('Erro ao carregar vaga');
      navigate('/m/jobs');
    } finally {
      setLoading(false);
    }
  };

  const checkSavedStatus = async () => {
    try {
      const response = await candidatesAPI.getSavedJobs();
      const savedIds = response.data?.map(j => j.job_id) || [];
      setSaved(savedIds.includes(jobId));
    } catch (error) {
      // Silently fail
    }
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      await jobsAPI.apply(jobId);
      setApplied(true);
      toast.success('Candidatura enviada com sucesso! 🎉');
    } catch (error) {
      if (error.response?.data?.detail?.includes('Already applied')) {
        setApplied(true);
        toast.info('Você já se candidatou a esta vaga');
      } else {
        toast.error(error.response?.data?.detail || 'Erro ao candidatar');
      }
    } finally {
      setApplying(false);
    }
  };

  const handleSaveJob = async () => {
    setSavingJob(true);
    try {
      if (saved) {
        await candidatesAPI.unsaveJob(jobId);
        setSaved(false);
        toast.success('Vaga removida dos salvos');
      } else {
        await candidatesAPI.saveJob(jobId);
        setSaved(true);
        toast.success('Vaga salva!');
      }
    } catch (error) {
      toast.error('Erro ao salvar vaga');
    } finally {
      setSavingJob(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  const hasInclusao = job?.inclusao_pcd || job?.inclusao_50_plus;
  const requisitos = job?.requisitos || job?.descricao?.split('\n').filter(line => line.trim().startsWith('-') || line.trim().startsWith('•'));

  return (
    <div className="min-h-screen bg-slate-50 pb-28" data-testid="mobile-job-detail">
      {/* Header */}
      <div className="bg-white sticky top-0 z-10 border-b border-slate-200">
        <div className="flex items-center gap-3 p-4">
          <button onClick={() => navigate('/m/jobs')} className="text-slate-600">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-lg font-bold text-slate-900 truncate flex-1">{job?.titulo}</h1>
          <button
            onClick={handleSaveJob}
            disabled={savingJob}
            className={`p-2 rounded-full transition-colors ${
              saved 
                ? 'bg-cyan-100 text-cyan-600' 
                : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
            }`}
          >
            {savingJob ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : saved ? (
              <BookmarkCheck className="w-5 h-5" />
            ) : (
              <Bookmark className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Company & Title */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Building2 className="w-8 h-8 text-slate-400" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  {job?.destaque && (
                    <Badge className="bg-amber-100 text-amber-700">
                      <Star className="w-3 h-3 mr-1" />
                      Destaque
                    </Badge>
                  )}
                  {hasInclusao && (
                    <Badge className="bg-purple-100 text-purple-700">
                      <Users className="w-3 h-3 mr-1" />
                      Inclusão
                    </Badge>
                  )}
                </div>
                <h2 className="text-xl font-bold text-slate-900">{job?.titulo}</h2>
                <p className="text-slate-600">{job?.company_name}</p>
              </div>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mt-4">
              <Badge variant="outline" className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {job?.localizacao}
              </Badge>
              <Badge variant="outline" className="flex items-center gap-1">
                <Briefcase className="w-3 h-3" />
                {job?.tipo_contrato}
              </Badge>
              <Badge variant="outline" className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {new Date(job?.created_at).toLocaleDateString('pt-BR')}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Description */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Target className="w-5 h-5 text-cyan-600" />
              Descrição da Vaga
            </h3>
            <p className="text-slate-600 whitespace-pre-line leading-relaxed">
              {job?.descricao}
            </p>
          </CardContent>
        </Card>

        {/* Requirements */}
        {job?.requisitos && (
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <ListChecks className="w-5 h-5 text-cyan-600" />
                Requisitos
              </h3>
              {Array.isArray(job.requisitos) ? (
                <ul className="space-y-2">
                  {job.requisitos.map((req, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      {req}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-slate-600 whitespace-pre-line">{job.requisitos}</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Desired DISC Profile */}
        {job?.perfil_disc && (
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-600" />
                Perfil Comportamental Desejado
              </h3>
              <div className="flex flex-wrap gap-2 mb-3">
                {job.perfil_disc.split('').map((trait, i) => {
                  const label = DISC_LABELS[trait.toUpperCase()];
                  return label ? (
                    <Badge key={i} className={label.color}>
                      {trait.toUpperCase()} - {label.name}
                    </Badge>
                  ) : null;
                })}
              </div>
              {job.perfil_disc_descricao && (
                <p className="text-sm text-slate-600">{job.perfil_disc_descricao}</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Inclusion Policies */}
        {hasInclusao && (
          <Card className="border-purple-200 bg-purple-50">
            <CardContent className="p-4">
              <h3 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Vaga com Políticas de Inclusão
              </h3>
              <p className="text-sm text-purple-700">
                Esta vaga faz parte do programa de diversidade e inclusão da empresa.
                {job.inclusao_pcd && " Candidatos PCD são bem-vindos."}
                {job.inclusao_50_plus && " Profissionais 50+ são bem-vindos."}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Expiration */}
        {job?.data_expiracao && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-900">Vaga expira em</p>
                  <p className="text-sm text-amber-700">
                    {new Date(job.data_expiracao).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Fixed Bottom Actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 space-y-2">
        {applied ? (
          <Button 
            className="w-full h-14 bg-emerald-600 hover:bg-emerald-700"
            disabled
          >
            <CheckCircle2 className="w-5 h-5 mr-2" />
            Candidatura Enviada
          </Button>
        ) : (
          <Button 
            className="w-full h-14 bg-cyan-600 hover:bg-cyan-700 text-lg"
            onClick={handleApply}
            disabled={applying}
          >
            {applying ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              'Candidatar-se Agora'
            )}
          </Button>
        )}
        <Button 
          variant="outline"
          className="w-full"
          onClick={handleSaveJob}
          disabled={savingJob}
        >
          {saved ? (
            <>
              <BookmarkCheck className="w-4 h-4 mr-2" />
              Vaga Salva
            </>
          ) : (
            <>
              <Bookmark className="w-4 h-4 mr-2" />
              Salvar Vaga
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
